import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import PhoneInput from './ui/PhoneInput';
import SiretInput from './ui/SiretInput';
import EmailInput from './ui/EmailInput';
import { AlertTriangle, Building2, Camera, CheckCircle, FileText, Info, Upload, X, HardHat, Paperclip } from 'lucide-react';

export const DeclarationFormSteps = ({
  step, formData, handleChange, photos, documents,
  handlePhotoUpload, removePhoto, handleDocumentUpload, removeDocument,
  acceptDeclaration, setAcceptDeclaration, memberData,
}) => {
  return (
    <>
        {step === 1 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Informations du Membre
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="raison_sociale">Raison sociale *</Label>
                    <Input
                      id="raison_sociale"
                      value={formData.raison_sociale}
                      onChange={(e) => handleChange('raison_sociale', e.target.value)}
                      placeholder="Nom de l'entreprise"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="forme_juridique">Forme juridique</Label>
                    <Select value={formData.forme_juridique} onValueChange={(v) => handleChange('forme_juridique', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sarl">SARL</SelectItem>
                        <SelectItem value="sas">SAS</SelectItem>
                        <SelectItem value="eurl">EURL</SelectItem>
                        <SelectItem value="ei">Entreprise Individuelle</SelectItem>
                        <SelectItem value="auto_entrepreneur">Auto-entrepreneur</SelectItem>
                        <SelectItem value="scop">SCOP</SelectItem>
                        <SelectItem value="scic">SCIC</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="siret">SIRET *</Label>
                    <SiretInput
                      value={formData.siret}
                      onChange={(val) => handleChange('siret', val)}
                      placeholder="14 chiffres"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="numero_adhesion">N° d'adhésion (le cas échéant)</Label>
                    <Input
                      id="numero_adhesion"
                      value={formData.numero_adhesion}
                      onChange={(e) => handleChange('numero_adhesion', e.target.value)}
                      placeholder="Numéro de membre"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="adresse_complete">Adresse complète</Label>
                  <Input
                    id="adresse_complete"
                    value={formData.adresse_complete}
                    onChange={(e) => handleChange('adresse_complete', e.target.value)}
                    placeholder="Adresse, Code postal, Ville"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="representant_legal">Représentant légal</Label>
                    <Input
                      id="representant_legal"
                      value={formData.representant_legal}
                      onChange={(e) => handleChange('representant_legal', e.target.value)}
                      placeholder="Nom et prénom"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone *</Label>
                    <PhoneInput
                      value={formData.telephone}
                      onChange={(val) => handleChange('telephone', val)}
                      placeholder="Numéro de téléphone"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => handleChange('email', val)}
                    placeholder="contact@entreprise.fr"
                    required
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Informations sur l'Opération */}
        {step === 2 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-orange-600" />
                  Informations sur l'Opération
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="intitule_chantier">Intitulé du chantier *</Label>
                  <Input
                    id="intitule_chantier"
                    value={formData.intitule_chantier}
                    onChange={(e) => handleChange('intitule_chantier', e.target.value)}
                    placeholder="Nom ou référence du chantier"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="adresse_travaux">Adresse des travaux *</Label>
                  <Input
                    id="adresse_travaux"
                    value={formData.adresse_travaux}
                    onChange={(e) => handleChange('adresse_travaux', e.target.value)}
                    placeholder="Lieu d'exécution des travaux"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="maitre_ouvrage">Maître d'ouvrage</Label>
                  <Input
                    id="maitre_ouvrage"
                    value={formData.maitre_ouvrage}
                    onChange={(e) => handleChange('maitre_ouvrage', e.target.value)}
                    placeholder="Nom du client / maître d'ouvrage"
                  />
                </div>

                <div>
                  <Label htmlFor="nature_travaux">Nature des travaux *</Label>
                  <Textarea
                    id="nature_travaux"
                    value={formData.nature_travaux}
                    onChange={(e) => handleChange('nature_travaux', e.target.value)}
                    placeholder="Décrivez la nature des travaux effectués..."
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date_debut">Date de début</Label>
                    <DatePickerFr
                      id="date_debut"
                      className="w-full mt-1"
                      value={formData.date_debut}
                      onChange={(v) => handleChange('date_debut', v)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_fin">Date de fin</Label>
                    <DatePickerFr
                      id="date_fin"
                      className="w-full mt-1"
                      value={formData.date_fin}
                      onChange={(v) => handleChange('date_fin', v)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Description du Dommage */}
        {step === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Description du Dommage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="description_dommage">Description détaillée *</Label>
                  <Textarea
                    id="description_dommage"
                    value={formData.description_dommage}
                    onChange={(e) => handleChange('description_dommage', e.target.value)}
                    placeholder="Décrivez en détail la nature du dommage, les circonstances de sa découverte, les parties concernées..."
                    rows={6}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.description_dommage.length}/50 caractères minimum
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Photos du dommage
                  </Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Cliquez pour ajouter des photos</p>
                      </div>
                      <input type="file" className="hidden" multiple accept="image/*" onChange={handlePhotoUpload} />
                    </label>
                  </div>
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                            <img 
                              src={URL.createObjectURL(photo)} 
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Documents joints
                  </Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center">
                        <FileText className="h-6 w-6 text-gray-400 mb-1" />
                        <p className="text-sm text-gray-500">Devis, factures, rapports...</p>
                      </div>
                      <input type="file" className="hidden" multiple onChange={handleDocumentUpload} />
                    </label>
                  </div>
                  {documents.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm truncate flex-1">{doc.name}</span>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="actions_entreprises">Actions déjà entreprises</Label>
                  <Textarea
                    id="actions_entreprises"
                    value={formData.actions_entreprises}
                    onChange={(e) => handleChange('actions_entreprises', e.target.value)}
                    placeholder="Mesures conservatoires, échanges avec les parties, prestataires contactés..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Demande au FOGEDOM-SCIC */}
        {step === 4 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  Demande au FOGEDOM-SCIC
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="type_demande">Type de demande *</Label>
                  <Select value={formData.type_demande} onValueChange={(v) => handleChange('type_demande', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner le type de demande" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expertise">Expertise / avis technique</SelectItem>
                      <SelectItem value="mediation">Médiation coopérative</SelectItem>
                      <SelectItem value="appui_operationnel">Appui opérationnel</SelectItem>
                      <SelectItem value="soutien_exceptionnel">Soutien exceptionnel (analyse)</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description_demande">Description de votre demande</Label>
                  <Textarea
                    id="description_demande"
                    value={formData.description_demande}
                    onChange={(e) => handleChange('description_demande', e.target.value)}
                    placeholder="Précisez votre demande d'accompagnement..."
                    rows={5}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Validation */}
        {step === 5 && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Validation et Envoi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Récapitulatif */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-700">Récapitulatif de votre déclaration</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Raison sociale:</span>
                    <span className="font-medium">{formData.raison_sociale}</span>
                    <span className="text-gray-500">Chantier:</span>
                    <span className="font-medium">{formData.intitule_chantier}</span>
                    <span className="text-gray-500">Type de demande:</span>
                    <span className="font-medium capitalize">{formData.type_demande.replace('_', ' ')}</span>
                    <span className="text-gray-500">Photos:</span>
                    <span className="font-medium">{photos.length} fichier(s)</span>
                    <span className="text-gray-500">Documents:</span>
                    <span className="font-medium">{documents.length} fichier(s)</span>
                  </div>
                </div>

                {/* Déclaration */}
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <p className="font-semibold mb-2">Déclaration importante</p>
                    <p className="text-sm">
                      Je reconnais que l'intervention éventuelle de COPPAM / FOGEDOM-SCIC 
                      n'est pas automatique et dépend d'une analyse individuelle ainsi que 
                      des ressources disponibles.
                    </p>
                  </AlertDescription>
                </Alert>

                <div className="flex items-start space-x-3 p-4 bg-white border rounded-lg">
                  <Checkbox
                    id="accept"
                    checked={acceptDeclaration}
                    onCheckedChange={setAcceptDeclaration}
                  />
                  <label htmlFor="accept" className="text-sm cursor-pointer">
                    J'ai lu et j'accepte les conditions ci-dessus. Je certifie que les informations 
                    fournies sont exactes et complètes.
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

    </>
  );
};
